import { Logic } from '~common/ui-logic/logic'
import { executeTask } from '~common/ui-logic/tasks'
import type { TaskState } from '~/services/cache/types'
import type { AppContextType } from '~/app-context'
import type { EntitlementFrontend } from '~common/google-auth/storage/types'
import { sleep } from '~common/utils/sleep'

export interface CheckoutSuccessfulLogicDependencies {}

export interface CheckoutSuccessfulLogicState {
    loadState: TaskState
    errorMessage?: string
    totalInvoiceCredits?: number
}

export class CheckoutSuccessfulLogic extends Logic<
    CheckoutSuccessfulLogicDependencies & AppContextType,
    CheckoutSuccessfulLogicState
> {
    getInitialState = (): CheckoutSuccessfulLogicState => ({
        loadState: 'pristine',
    })

    private broadcastChannel?: BroadcastChannel

    async initialize() {
        await executeTask(this, 'loadState', async () => {
            const userEmail = localStorage.getItem('user-email')
            const user = await this.deps.services.auth.getLoggedInUser()
            if (user) {
                const now = Date.now()
                let entitlement: EntitlementFrontend | null = null
                let attempts = 0
                const maxAttempts = 5
                const delayMs = 2000 // 2 seconds between retries

                while (attempts < maxAttempts) {
                    entitlement =
                        await this.deps.storageModules.entitlements.getEntitlement(
                            user.uid,
                        )

                    if (
                        entitlement &&
                        entitlement.updatedAt &&
                        now - entitlement.updatedAt <= 600000
                    ) {
                        // Compute total remaining invoice credits directly from entitlement
                        const totalCredits =
                            (entitlement.invoicesRemainingSubscription ?? 0) +
                            (entitlement.invoicesRemainingConsumable ?? 0)
                        this.setState({ totalInvoiceCredits: totalCredits })
                        break
                    }
                    attempts++
                    if (attempts <= maxAttempts) {
                        await sleep(delayMs)
                    } else {
                        throw new Error(
                            'Entitlement update not detected within expected time. Please contact support.',
                        )
                    }
                }
            } else if (userEmail) {
                const statusResult =
                    await this.deps.services.entitlements.getLemonSqueezyStatus(
                        userEmail,
                    )
                const { lemonsqueezyUserId } = statusResult
                if (lemonsqueezyUserId) {
                    location.href = '/?login'
                }
            }
        })

        // Broadcast success to other tabs if credits loaded
        if (this.state.totalInvoiceCredits !== undefined) {
            this.broadcastChannel = new BroadcastChannel('taxing-checkout')
            this.broadcastChannel.postMessage({
                type: 'checkout-successful',
                totalInvoiceCredits: this.state.totalInvoiceCredits,
                timestamp: Date.now(),
            })
            this.broadcastChannel.close()
            this.broadcastChannel = undefined
        }
    }
}
